import { CommonModule } from "@angular/common";
import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { ISwapRequestEntity } from "@app/shared/swap-request/interfaces/swap-request.interface";
import { IChatMessage } from "@app/shared/chat/interfaces/chat-message.interface";
import { ChatSocketService } from "@app/shared/chat/services/chat-socket.service";
import { IUnresolvedConversation } from "@app/shared/chat/interfaces/conversation.interface";
import { EChatMessageType } from "@app/shared/chat/enums/chat-message-type.enum";
import { ConversationsListComponent } from "@app/applications/chat/components/conversations-list/conversations-list.component";
import { ButtonComponent } from "@kernel/shared/modules/button/components/button/button.component";
import { RouterLink } from "@angular/router";
import { ChatService } from "@app/shared/chat/services/chat.service";
import { MobileDetectionService } from "@app/shared/general/services/mobile-detection.service";

@Component({
  selector: "app-chat-box",
  imports: [CommonModule, TranslateModule, FormsModule, ConversationsListComponent, ButtonComponent, RouterLink],
  templateUrl: "./chat-box.component.html",
  styleUrl: "./chat-box.component.scss",
})
export class ChatBoxComponent implements OnInit, OnChanges {
  @ViewChild("messagesContainer") private messagesContainer!: ElementRef<HTMLElement>;
  @Output() viewProperty = new EventEmitter<void>();

  chatSocketService = inject(ChatSocketService);
  chatService = inject(ChatService);
  mobileService = inject(MobileDetectionService);

  swapRequest = input<ISwapRequestEntity>();
  resolvedMessages = input<IChatMessage[]>();
  loading = signal(false);
  messages = signal<IChatMessage[]>([]);

  // State properties
  messageText = "";
  isSendButtonDisabled = true;
  showChatList = false;
  isDropdownOpen = false;

  constructor() {
    effect(() => {
      if (this.mobileService.isMobile() && !this.swapRequest() && this.chatService.conversations().length > 0) {
        this.showChatList = true;
      }
    });
  }

  /**
   * Component Lifecycle and Chat Handling
   */
  ngOnInit() {
    // Subscribe to incoming chat messages from the socket service.
    // Whenever a new message arrives, `handleIncomingMessage` is called.
    this.chatSocketService.observerChatMessagesEvent().subscribe({
      next: data => this.handleIncomingMessage(data),
    });
  }

  /**
   * Angular Lifecycle Hook - Detects input property changes
   * @param changes - Object containing changed properties
   */
  ngOnChanges(changes: SimpleChanges) {
    // If the resolvedMessages input changes, update the messages signal
    if (changes["resolvedMessages"]) {
      this.messages.set(this.resolvedMessages()?.reverse() ?? []);
    }
    // Note: I needed to handle it like this in order to handle navigation change to different conversation.
    setTimeout(() => this.scrollToBottom());
  }

  /**
   * Enables or disables the send button based on input text.
   */
  checkInput() {
    this.isSendButtonDisabled = !this.messageText.trim();
  }

  /**
   * Sends a chat message through the socket service.
   * Creates a pending message immediately and updates it after acknowledgment.
   */
  async sendMessage(): Promise<void> {
    if (!this.messageText.trim()) return;

    // Push a pending message to the chat immediately
    const msg = this.pushPending(this.messageText);

    // Send message via socket
    // TODO: handle empty case.
    this.chatSocketService
      .sendChatMessage({
        content: this.messageText,
        targetSwapRequestId: this.swapRequest()?.id ?? "",
      })
      .catch(() => undefined)
      .then(ack => this.updatePendingMessage(msg, ack?.result));

    // Clear input field
    this.messageText = "";
  }

  /**
   * Updates a previously pushed pending message with the server acknowledgment.
   * If update is undefined, mark message as errored.
   *
   * @param msg - Original pending message
   * @param update - Updated message from server
   */
  updatePendingMessage(msg: IChatMessage, update?: IChatMessage) {
    this.messages.update(list => list.map(m => (m.id === msg.id ? { ...m, ...(update ?? { errored: true }) } : m)));
  }

  /**
   * Creates and pushes a pending message to the messages list.
   *
   * @param content - Message content
   * @returns The created pending message
   */
  private pushPending(content: string): IChatMessage {
    const msg = this.createPendingMessage(content);
    this.pushMessage(msg);
    return msg;
  }

  /**
   * Generates a new pending chat message object.
   *
   * @param content - Message content
   * @returns A new pending IChatMessage
   */
  private createPendingMessage(content: string): IChatMessage {
    const now = new Date();
    return {
      id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 10),
      content,
      createdAt: now,
      updatedAt: now,
      type: EChatMessageType.TEXT,
      isSeen: true,
      senderSwapRequest: "ME",
    };
  }

  /**
   * Adds a message to the chat messages signal and scrolls to bottom.
   *
   * @param message - Message to push
   */
  pushMessage(message: IChatMessage) {
    this.messages.set([...this.messages(), message]);
    setTimeout(() => this.scrollToBottom());
  }

  /**
   * Handles incoming messages from the socket service.
   * Only adds messages that belong to the current conversation.
   *
   * @param data - Incoming message payload containing conversation info
   */
  handleIncomingMessage(data: { conversation: IUnresolvedConversation; message: IChatMessage }) {
    if (data.message.senderSwapRequest !== this.swapRequest()?.id) {
      return; // Ignore messages not related to the current conversation
    }
    this.pushMessage(data.message);
    // TODO handle the empty case
    this.chatSocketService.markMessagesAsSeen(this.swapRequest()?.id ?? "");
  }

  /**
   * Scrolls the chat messages container to the bottom.
   */
  private scrollToBottom(): void {
    if (this.messagesContainer?.nativeElement) {
      this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
    }
  }

  // TODO: check the fallowing

  toggleChatList() {
    this.showChatList = !this.showChatList;
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  openPropertyView() {
    this.viewProperty.emit();
    this.isDropdownOpen = false;
  }
}
